import React, { useContext, useState, useEffect } from 'react';
import { UserContext, CompanyContext } from '../App';
import { Link, useLocation } from 'react-router-dom';
import ProtectedText, { PROTECTED_TEXTS } from './ProtectedText';
import CompanySwitcher from './CompanySwitcher';
import TextGuard, { SectionNameGuard } from './TextGuard';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Home, 
  Package, 
  ShoppingCart, 
  RotateCcw, 
  Wallet,
  Package2,
  FileText, 
  User, 
  Users,
  LogOut,
  Menu,
  X,
  Croissant,
  Settings,
  ChevronDown,
  UserCheck,
  UserPlus,
  Download,
  Upload,
  Database,
  Building2,
  Receipt
} from 'lucide-react';

const Navigation = () => {
  const { user, handleLogout } = useContext(UserContext);
  const { company } = useContext(CompanyContext);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [manageMenuOpen, setManageMenuOpen] = useState(false);

  // Fermer le menu quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (manageMenuOpen && !event.target.closest('[data-testid="nav-manage"]')) {
        setManageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [manageMenuOpen]);

  const getNavigationForUser = () => {
    if (!user) return [];
    
    const baseNavigation = [
      {
        name: PROTECTED_TEXTS.DASHBOARD,
        href: '/dashboard',
        icon: Home,
        allowedRoles: ['staff', 'manager', 'owner', 'system_admin']
      },
      {
        name: PROTECTED_TEXTS.STOCK,
        href: '/stock',
        icon: Package2,
        allowedRoles: ['staff', 'manager', 'owner', 'system_admin']
      },
      {
        name: PROTECTED_TEXTS.PRODUCTION,
        href: '/production',
        icon: Package,
        allowedRoles: ['staff', 'manager', 'owner', 'system_admin']
      },
      {
        name: PROTECTED_TEXTS.VENTES,
        href: '/sales',
        icon: ShoppingCart,
        allowedRoles: ['manager', 'owner', 'system_admin'] // COMMIS EXCLU
      },
      {
        name: PROTECTED_TEXTS.RETOURS,
        href: '/returns',
        icon: RotateCcw,
        allowedRoles: ['staff', 'manager', 'owner', 'system_admin']
      },
      {
        name: PROTECTED_TEXTS.DEPENSES,
        href: '/expenses',
        icon: Wallet,
        allowedRoles: ['manager', 'owner', 'system_admin'] // COMMIS EXCLU
      },
      {
        name: PROTECTED_TEXTS.RAPPORTS,
        href: '/reports',
        icon: FileText,
        allowedRoles: ['staff', 'manager', 'owner', 'system_admin']
      },
      {
        name: 'Factures',
        href: '/invoices',
        icon: Receipt,
        allowedRoles: ['manager', 'owner', 'system_admin']
      }
    ];
    
    // Filtrer la navigation selon le rôle de l'utilisateur
    return baseNavigation.filter(item => 
      item.allowedRoles.includes(user.role)
    );
  };

  // Navigation admin uniquement
  const adminNavigation = [
    {
      name: 'Utilisateurs',
      href: '/users',
      icon: Users,
    }
  ];

  const isAdmin = () => {
    return user && (user.role === 'owner' || user.role === 'admin');
  };

  const isSystemAdmin = () => {
    // Seul l'administrateur système peut accéder à "Gérer"
    return user && user.role === 'system_admin';
  };

  const manageMenuItems = [
    {
      section: 'Utilisateurs',
      items: [
        {
          name: 'Gestion des utilisateurs',
          href: '/users',
          icon: Users,
          description: 'Voir tous les utilisateurs'
        },
        {
          name: 'Demandes d\'approbation',
          href: '/users/pending',
          icon: UserCheck,
          description: 'Approuver les nouvelles demandes'
        }
      ]
    },
    {
      section: 'Entreprise',
      items: [
        {
          name: 'Paramètres de l\'entreprise',
          href: '/company/settings',
          icon: Building2,
          description: 'Modifier les informations'
        }
      ]
    },
    {
      section: 'Paramètres du système',
      items: [
        {
          name: 'Sauvegarde du système',
          href: '/system/backup',
          icon: Download,
          description: 'Exporter les données'
        },
        {
          name: 'Restauration du système',
          href: '/system/restore',
          icon: Upload,
          description: 'Importer les données'
        }
      ]
    }
  ];

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'system_admin':
        return 'Administrateur du système';
      case 'owner':
        return 'Propriétaire';
      case 'manager':
        return 'Gérant';
      case 'staff':
        return 'Commis';
      default:
        return role;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'system_admin':
        return 'bg-red-100 text-red-800';
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      case 'staff':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <div className="flex items-center space-x-4">
            <div className="h-10 w-10 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg flex items-center justify-center overflow-hidden">
              {company && company.logo ? (
                <img 
                  src={company.logo} 
                  alt="Logo entreprise" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <Croissant className="h-6 w-6 text-white" />
              )}
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-gray-900" spellCheck="false">
                {company ? company.raison_sociale : 'Boulangerie Pro'}
              </h1>
              <p className="text-xs text-gray-600" spellCheck="false">
                Suivi de Production
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {getNavigationForUser().map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                    isActive
                      ? 'bg-orange-100 text-orange-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  spellCheck="false"
                  autoCorrect="off"
                  autoCapitalize="off"
                >
                  <Icon className="h-4 w-4" />
                  <SectionNameGuard section={item.name} />
                </Link>
              );
            })}
            
            {/* Menu Gérer - Visible uniquement pour l'administrateur système */}
            {user && user.role === 'system_admin' && (
              <div className="relative">
                <button
                  onClick={() => setManageMenuOpen(!manageMenuOpen)}
                  className={`px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 flex items-center space-x-3 admin-ripple transform hover:scale-105 ${
                    manageMenuOpen
                      ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 shadow-xl scale-105 border-2 border-orange-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 hover:shadow-lg border-2 border-transparent hover:border-orange-200'
                  }`}
                  data-testid="nav-manage"
                >
                  <div className="admin-icon p-2 bg-white rounded-lg shadow-md">
                    <Settings className="h-5 w-5 icon-bounce" />
                  </div>
                  <ProtectedText>Gérer</ProtectedText>
                  <ChevronDown className={`h-4 w-4 transition-all duration-500 ${manageMenuOpen ? 'rotate-180 text-orange-600 scale-125' : 'rotate-0'}`} />
                </button>
                
                {/* Dropdown Menu */}
                {manageMenuOpen && (
                  <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 py-3 z-50 dropdown-enhanced">
                    {manageMenuItems.map((section, sectionIndex) => (
                      <div key={section.section}>
                        {sectionIndex > 0 && <div className="border-t border-gray-100 my-3 mx-4"></div>}
                        
                        {/* Section Header */}
                        <div className="px-5 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider bg-gradient-to-r from-gray-50 to-gray-100 mx-3 rounded-lg">
                          <ProtectedText>{section.section}</ProtectedText>
                        </div>
                        
                        {/* Section Items */}
                        <div className="mt-2 space-y-1">
                          {section.items.map((item) => {
                            const ItemIcon = item.icon;
                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                className="admin-menu-item flex items-center px-5 py-4 text-sm text-gray-700 rounded-lg mx-3 group"
                                onClick={() => setManageMenuOpen(false)}
                                data-testid={`manage-${item.name.toLowerCase().replace(/[ ']/g, '-')}`}
                              >
                                <div className="admin-icon flex items-center justify-center w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl mr-4 group-hover:from-orange-100 group-hover:to-amber-100 shadow-lg">
                                  <ItemIcon className="h-6 w-6 text-gray-500 group-hover:text-orange-600" />
                                </div>
                                <div className="flex-1">
                                  <p className="font-bold no-transform group-hover:text-orange-700 text-base" spellCheck="false">
                                    <ProtectedText>{item.name}</ProtectedText>
                                  </p>
                                  <p className="text-xs text-gray-500 no-transform group-hover:text-orange-600 mt-1" spellCheck="false">
                                    <ProtectedText>{item.description}</ProtectedText>
                                  </p>
                                  <div className="admin-badge w-2 h-2 bg-orange-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-1"></div>
                                </div>
                                <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                                  <div className="w-3 h-3 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full animate-pulse"></div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    
                    {/* Footer du menu avec animation */}
                    <div className="mt-4 mx-3 px-2 py-2 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                      <p className="text-xs text-orange-700 text-center font-medium">
                        🛡️ Administration Système
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Company Switcher pour Multi-entreprises */}
            <CompanySwitcher />
            
            {/* Paramètres Entreprise pour Propriétaire */}
            {user && user.role === 'owner' && company && (
              <Link
                to="/company/settings"
                className="hidden sm:flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
                title="Paramètres de l'entreprise"
              >
                <Building2 className="h-4 w-4" />
                <span className="font-medium">Mon Entreprise</span>
              </Link>
            )}
            
            {/* Accès secret à l'audit pour system_admin */}
            {user && user.role === 'system_admin' && (
              <div className="hidden">
                <Link
                  to="/system/audit-trail-secret"
                  className="text-xs text-red-500 hover:text-red-600"
                  title="Journal d'audit système (confidentiel)"
                >
                  🕵️
                </Link>
              </div>
            )}
            
            {/* User Info */}
            <div className="hidden sm:flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  {user?.username}
                </p>
                <Badge className={`text-xs ${getRoleBadgeColor(user?.role)}`}>
                  {getRoleDisplayName(user?.role)}
                </Badge>
              </div>
              <div className="h-8 w-8 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-white" />
              </div>
            </div>

            {/* Logout Button */}
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="hidden sm:flex items-center space-x-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              <span>Déconnexion</span>
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4 space-y-2">
            {getNavigationForUser().map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-orange-100 text-orange-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <ProtectedText>{item.name}</ProtectedText>
                </Link>
              );
            })}
            
            {/* Menu Gérer Mobile - Administrateur système uniquement */}
            {user && user.role === 'system_admin' && (
              <>
                <div className="border-t border-gray-200 my-2"></div>
                <div className="px-4 py-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    <ProtectedText>Administration</ProtectedText>
                  </p>
                  
                  {manageMenuItems.map((section) => (
                    <div key={section.section} className="mb-4">
                      <p className="text-xs font-medium text-gray-600 mb-2 no-transform" spellCheck="false">
                        <ProtectedText>{section.section}</ProtectedText>
                      </p>
                      {section.items.map((item) => {
                        const ItemIcon = item.icon;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className="flex items-center space-x-4 px-4 py-4 rounded-lg text-sm text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 hover:text-gray-900 transition-all duration-200 group"
                            onClick={() => setMobileMenuOpen(false)}
                            data-testid={`mobile-manage-${item.name.toLowerCase().replace(/[ ']/g, '-')}`}
                          >
                            <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg group-hover:bg-orange-100 group-hover:scale-110 transition-all duration-200">
                              <ItemIcon className="h-5 w-5 text-gray-500 group-hover:text-orange-600 transition-colors duration-200" />
                            </div>
                            <div>
                              <p className="font-semibold no-transform group-hover:text-orange-700 transition-colors duration-200" spellCheck="false">
                                <ProtectedText>{item.name}</ProtectedText>
                              </p>
                              <p className="text-xs text-gray-500 no-transform group-hover:text-orange-600 transition-colors duration-200" spellCheck="false">
                                <ProtectedText>{item.description}</ProtectedText>
                              </p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {/* Mobile User Info */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center justify-between px-4 py-2">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {user?.username}
                    </p>
                    <Badge className={`text-xs ${getRoleBadgeColor(user?.role)}`}>
                      {getRoleDisplayName(user?.role)}
                    </Badge>
                  </div>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  size="sm"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;